import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Select, Space } from "antd"
import { useOutletContext } from "react-router-dom"

import { useAppContext } from "@components/AppContext"
import { getActiveProjects } from "@components/Project"

import listProjectFiles from "./helpers/listProjectFiles"
import listProjectInvestments from "./helpers/listProjectInvestments"
import listProjectTransactions from "./helpers/listProjectTransactions"

const LABEL_TRANSFER_TO = "Transfer to"
const LABEL_PLACEHOLDER = "Select a project"


const TargetProjectSelect = ({
  targetProjectId = undefined,
  sourceProjectId = undefined,
  setTargetProjectId,
  setTargetFiles,
  setTargetBatches,
  setTargetInvestments,
  setTargetTransactions,
}) => {
  const [ isLoading, setIsLoading ] = useState(false)

  const { request } = useAppContext()
  const { projects } = useOutletContext()

  const targetProjectOptions = useMemo(() =>
    getActiveProjects(projects)
      .filter(({ id }) => id !== sourceProjectId)
      .map(({ id, name }) => ({ label: name, value: id }))
  , [ projects, sourceProjectId ])

  const onChange = async (projectId) => {
    setTargetProjectId(projectId)
    setIsLoading(true)

    const filesPromise = listProjectFiles(request, projectId)
      .then(([ batches, files ]) => {
        setTargetFiles(files)
        setTargetBatches(batches)
      })

    const investmentsPromise = listProjectInvestments(request, projectId)
      .then(investments => setTargetInvestments(investments))

    const transactionsPromise = listProjectTransactions(request, projectId)
      .then(transactions => setTargetTransactions(transactions))

    await Promise.all([
      filesPromise,
      investmentsPromise,
      transactionsPromise,
    ])

    setIsLoading(false)
  }

  return (
    <Space>
      <span>
        {LABEL_TRANSFER_TO}:
      </span>

      <Select
        showSearch
        allowClear
        value={targetProjectId}
        options={targetProjectOptions}
        onChange={onChange}
        placeholder={LABEL_PLACEHOLDER}
        optionFilterProp="label"
        loading={isLoading}
        disabled={isLoading}
      />
    </Space>
  )
}

TargetProjectSelect.propTypes = {
  sourceProjectId: PropTypes.string,
  targetProjectId: PropTypes.string,
  setTargetProjectId: PropTypes.func.isRequired,
  setTargetFiles: PropTypes.func.isRequired,
  setTargetBatches: PropTypes.func.isRequired,
  setTargetInvestments: PropTypes.func.isRequired,
  setTargetTransactions: PropTypes.func.isRequired,
}

export default TargetProjectSelect
